import type {
  Geofence,
  MapMarker,
  MapMarkerIconOptions,
} from '@gpx-spa/geo-domain';
import {
  getPopupContent,
  trackerIcon,
} from '@/components/device/map/utils/marker';
import { METERS_PER_MILE, unitShortLabel } from '@gpx-spa/ui';
import type {
  FindContainersResult,
  FindContainersSite,
  FindContainersTracker,
} from '@gpx-spa/queries/src/getAccountFindContainers';

export type FindContainersDistanceUnit = 'miles' | 'km';

/** Neither the account nor the user has a distance-unit setting yet; the customer app reads miles everywhere. */
export const FIND_CONTAINERS_DISTANCE_UNIT: FindContainersDistanceUnit =
  'miles';

export function radiusToMeters(
  value: number | null | undefined,
  unit: FindContainersDistanceUnit,
): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * (unit === 'miles' ? METERS_PER_MILE : 1000));
}

export function formatSiteDistance(
  meters: number | null,
  unit: FindContainersDistanceUnit,
): string {
  if (meters == null) return '–';
  const value = meters / (unit === 'miles' ? METERS_PER_MILE : 1000);
  const shown = value < 10 ? value.toFixed(1) : String(Math.round(value));
  return `${shown} ${unitShortLabel(unit)}`;
}

export function formatTarget(min: number | null, max: number | null): string {
  if (min == null && max == null) return 'target not set';
  if (max == null) return `target ${min}+`;
  const low = min ?? 0;
  return low === max ? `target ${max}` : `target ${low}–${max}`;
}

export type StockLevel = 'under-min' | 'over-max' | 'in-range';

export type StockBar = {
  fillPct: number;
  minPct: number | null;
  maxPct: number | null;
  level: StockLevel;
};

export function stockBarScale(sites: FindContainersSite[]): number {
  return Math.max(
    1,
    ...sites.map((s) =>
      Math.max(s.units, s.min_assets ?? 0, s.max_assets ?? 0),
    ),
  );
}

export function stockLevel(site: FindContainersSite): StockLevel {
  const { units, min_assets: min, max_assets: max } = site;
  if (max != null && units > max) return 'over-max';
  if (min != null && units < min) return 'under-min';
  return 'in-range';
}

export function stockBar(site: FindContainersSite, scale: number): StockBar {
  const pct = (value: number) => Math.min(100, (value / scale) * 100);
  const { units, min_assets: min, max_assets: max } = site;
  return {
    fillPct: pct(units),
    minPct: min == null ? null : pct(min),
    maxPct: max == null ? null : pct(max),
    level: stockLevel(site),
  };
}

export function siteGeofence(site: FindContainersSite): Geofence | null {
  const { geometry } = site;
  if (!geometry) return null;
  if (geometry.type === 'circle') {
    return {
      id: site.geofence_id,
      name: site.name,
      type: 'radius',
      lat: geometry.lat,
      lng: geometry.lng,
      radius: geometry.radius_m,
    };
  }
  const ring = geometry.points.map(([lat, lng]) => [lng, lat]);
  const [first, last] = [ring[0], ring[ring.length - 1]];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([...first]);
  }
  return {
    id: site.geofence_id,
    name: site.name,
    type: 'custom',
    lat: geometry.lat,
    lng: geometry.lng,
    geo_json: { type: 'Polygon', coordinates: [ring] },
  };
}

export function searchRadiusGeofence(
  destination: { lat: number; lng: number } | null,
  radiusMeters: number | null,
): Geofence | null {
  if (!destination || radiusMeters == null) return null;
  return {
    id: 0,
    name: 'Search radius',
    type: 'radius',
    lat: destination.lat,
    lng: destination.lng,
    radius: radiusMeters,
  };
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function siteTooltipHtml(site: FindContainersSite): string {
  const units = `${site.units} ${site.units === 1 ? 'unit' : 'units'}`;
  return `<div class="font-semibold">${escapeHtml(site.name)}</div><div>${units}</div>`;
}

export function siteMarkerId(site: FindContainersSite): string {
  return `site-${site.geofence_id}`;
}

const SITE_CIRCLE_SIZE = 26;
export const SITE_TOOLTIP_OFFSET: [number, number] = [0, -SITE_CIRCLE_SIZE / 2];

export function siteMarkers(sites: FindContainersSite[]): MapMarker[] {
  return sites.flatMap((site) =>
    site.geometry
      ? [
          {
            id: siteMarkerId(site),
            position: { lat: site.geometry.lat, lng: site.geometry.lng },
            style: {
              kind: 'circle',
              fillColor: '#ffffff',
              borderColor: '#F25A54',
              label: String(site.units),
              textColor: '#1d2e30',
              sizePx: SITE_CIRCLE_SIZE,
              fontSizePx: 11,
            },
          },
        ]
      : [],
  );
}

export function trackerMarkerId(tracker: FindContainersTracker): string {
  return `tracker-${tracker.device_id}`;
}

const TRACKER_PIN_SIZE: [number, number] = [28, 36];

/**
 * Where the trackers page opens its popup: Leaflet's 7px popup offset plus the pin's -31px popup anchor.
 * Opened at the pin's tip instead, the tooltip covers the pointer and flickers.
 */
export const TRACKER_TOOLTIP_OFFSET: [number, number] = [0, -24];
const TRACKER_PIN_SHADOW_SIZE: [number, number] = [42, 45];

/** The tracker as a trackers-page current position, so that page's pin and popup code can draw it. */
function asCurrentPosition(tracker: FindContainersTracker) {
  return {
    location: [tracker.lng, tracker.lat],
    timestamp: tracker.updated_time ?? tracker.timestamp,
    fix: tracker.fix,
    last_movement_time: tracker.last_movement_time ?? undefined,
    is_inside_geofences: tracker.geofence_ids.length > 0,
    address: tracker.address,
    battery: tracker.battery,
    temperature: tracker.temperature,
    humidity: tracker.humidity,
  };
}

export function trackerTooltipHtml(tracker: FindContainersTracker): string {
  return getPopupContent({ name: tracker.name }, asCurrentPosition(tracker));
}

/** The trackers page's pin for the same position, from its BLE set (ENG-2955 retires the other set). */
export function trackerPin(
  tracker: FindContainersTracker,
): MapMarkerIconOptions {
  const { options } = trackerIcon({}, asCurrentPosition(tracker), {
    isNewIcon: true,
  });
  return {
    url: options.iconUrl,
    size: TRACKER_PIN_SIZE,
    anchor: options.iconAnchor,
    shadowUrl: options.shadowUrl,
    shadowSize: TRACKER_PIN_SHADOW_SIZE,
    shadowAnchor: options.shadowAnchor,
  };
}

export function trackerMarkers(trackers: FindContainersTracker[]): MapMarker[] {
  return trackers.map((t) => ({
    id: trackerMarkerId(t),
    position: { lat: t.lat, lng: t.lng },
    style: { kind: 'icon', icon: trackerPin(t) },
  }));
}

export type FindContainersSearchOutcome =
  | 'sites'
  | 'no-trackers'
  | 'none-within-radius'
  | 'none-at-locations';

export type FindContainersEmptyState =
  | 'no-container-types'
  | Exclude<FindContainersSearchOutcome, 'sites'>;

export function searchOutcome(
  result: FindContainersResult,
  radiusMeters: number | null,
): FindContainersSearchOutcome {
  if (result.sites.length > 0) return 'sites';
  if (radiusMeters != null) return 'none-within-radius';
  if (result.trackers.length === 0) return 'no-trackers';
  return 'none-at-locations';
}

export function findContainersEmptyState({
  containerTypeCount,
  typeTrackerCount,
  result,
  radiusMeters,
}: {
  containerTypeCount: number;
  typeTrackerCount: number | null;
  result: FindContainersResult | null | undefined;
  radiusMeters: number | null;
}): FindContainersEmptyState | null {
  if (containerTypeCount === 0) return 'no-container-types';
  if (typeTrackerCount === 0) return 'no-trackers';
  if (!result) return null;
  const outcome = searchOutcome(result, radiusMeters);
  return outcome === 'sites' ? null : outcome;
}

export type FindContainersSort = 'distance' | 'units';

export function sortSites(
  sites: FindContainersSite[],
  sort: FindContainersSort,
): FindContainersSite[] {
  if (sort === 'distance') return sites;
  return [...sites].sort(
    (a, b) =>
      b.units - a.units ||
      (a.distance_m ?? Infinity) - (b.distance_m ?? Infinity),
  );
}

export type FindContainersFilters = {
  containerTypeId: number | null;
  destinationId: number | null;
  radius: number | null;
  sort: FindContainersSort;
};

type RouteQuery = Record<string, unknown>;

function queryId(value: unknown): number | null {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function filtersFromQuery(
  query: RouteQuery,
  radiusChoices: number[],
): FindContainersFilters {
  const radius = Number(query.radius);
  return {
    containerTypeId: queryId(query.type),
    destinationId: queryId(query.destination),
    radius:
      query.radius === 'any'
        ? null
        : radiusChoices.includes(radius)
          ? radius
          : radiusChoices[0],
    sort: query.sort === 'units' ? 'units' : 'distance',
  };
}

export function filtersToQuery(
  filters: FindContainersFilters,
): Record<string, string> {
  return {
    ...(filters.containerTypeId != null && {
      type: String(filters.containerTypeId),
    }),
    ...(filters.destinationId != null && {
      destination: String(filters.destinationId),
    }),
    radius: filters.radius == null ? 'any' : String(filters.radius),
    ...(filters.sort !== 'distance' && { sort: filters.sort }),
  };
}
